import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';

import { minDesktop, mediaPhone, minTablet } from '../styles/breakpoints';
import { GrayBack, White } from '../styles/colors';
import { LogoLabelStyles as CommonLogoLabelStyles } from '../styles/Logo';

import BALogo from '../BALogo';
import BFBLogo from '../BFBLogo';
import H2 from '../H2';
import Stacheldraht from '../Stacheldraht';

import RhombusOutlineTopImg from '../rhombus/rhombus-outline-top.svg';
import RhombusOutlineMiddleImg from '../rhombus/rhombus-outline-middle.svg';
import RhombusOutlineBottomImg from '../rhombus/rhombus-outline-bottom.svg';
import RhombusFlatTopImg from '../rhombus/rhombus-flat-top.svg';
import RhombusFlatBottomImg from '../rhombus/rhombus-flat-bottom.svg';

import CareLogoImg from './images/CARE-Logo.png';
import KiwiLogoImg from './images/kiwi.png';

const RHOMBUS_PHONE_HEIGHT = 20;
const RHOMBUS_TABLET_HEIGHT = 40;
const RHOMBUS_DESKTOP_HEIGHT = 64;
const RHOMBUS_DESKTOP_WIDTH = 1180;

const Container = styled.div`
  background-color: ${GrayBack};  

  ${mediaPhone} {
    margin-bottom: -30px;    
  }

  ::after {
    content: '';
    display: block;
    left: 0;
    right: 0;
    bottom: 0;
    height: ${RHOMBUS_PHONE_HEIGHT}px;
    background-color: ${White};
    background-size: 100% 100%;
    background-repeat: no-repeat;
    background-image: url(${RhombusFlatBottomImg});
  }

  ${minTablet} {
    margin-left: 15px;
    margin-right: 15px;
    margin-bottom: -20px;    

    ::after {
      height: ${RHOMBUS_TABLET_HEIGHT}px;
    }
  }

  ${minDesktop} {
    width: ${RHOMBUS_DESKTOP_WIDTH}px;

    margin-top: -40px;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: 0;    

    position: relative;
    top: 0;
    left: 25px;
    
    background-color: transparent;  

    ::after {
      height: ${RHOMBUS_DESKTOP_HEIGHT}px;
      background-image: url(${RhombusOutlineBottomImg});
    }
  }
`;

const Before = styled.div`
  height: ${RHOMBUS_PHONE_HEIGHT}px;

  background-size: 100% 100%;
  background-repeat: no-repeat;
  background-color: ${White};
  background-image: url(${RhombusFlatTopImg});

  ${minTablet} {
    height: ${RHOMBUS_TABLET_HEIGHT}px;
  }

  ${minDesktop} {
    height: ${RHOMBUS_DESKTOP_HEIGHT}px;
    width: ${RHOMBUS_DESKTOP_WIDTH}px;
    background-color: transparent;
    background-image: url(${RhombusOutlineTopImg});
  }
`;

const Content = styled.article`
  padding: 0 20px 40px;

  ${minTablet} {
    padding: 20px 40px;
  }

  ${minDesktop} {
    width: ${RHOMBUS_DESKTOP_WIDTH}px;

    padding: 40px 80px;

    background-color: ${White};
    background-size: 100% 100%;
    background-repeat: no-repeat;
    background-image: url(${RhombusOutlineMiddleImg});
  }
`;

const StacheldrahtStyled = styled(Stacheldraht)`
  display: none;
  ${mediaPhone} {
    display: block;
    margin-bottom: -10px;
  }
`;

const Title = styled(H2)`
  line-height: 1;
  margin-bottom: 20px;
  ${minTablet} {
    line-height: 2;
    margin-bottom: 30px;
  }
`;

const InnerContent = styled.div`
  ${minTablet} {
    display: flex;
  }
  ${minDesktop} {
    padding-left: 150px;
  }
`;

const AboutThisProject = styled.div`
  line-height: 1.3;

  font-size: 16px;

  ${minTablet} {
    font-size: 18px;

    order: 2;
  }
`;

const Logos = styled.div`
  margin-top: 30px;

  ${minTablet} {
    margin-top: 2px;
    margin-right: 40px;
  }
  ${minDesktop} {
    margin-right: 70px;
  }
`;

const LogoMargins = css`
  margin-top: 35px;
  ${minTablet} {
    margin-top: 50px;
  }
`;

const BALogoStyled = styled(BALogo)`
  ${LogoMargins};
`;

const LogoAlignStyles = css`
  text-align: center;
  ${minTablet} {
    text-align: right;
  }
`;

const LogoImageContainerStyles = css`
  ${mediaPhone} {
    text-align: center;
  }
`;

const Fachpartner = styled.article`
  ${LogoMargins};
  ${LogoAlignStyles};
`;

const FachpartnerLabel = styled.div``;

const FachpartnerLogoContainer = styled.div``;

const FachpartnerLink = styled.a``;

const CareLogo = styled.img`
  max-height: 50px;
`;

const KiwiLogo = styled.img`
  margin-top: 14px;
  max-height: 60px;
  ${mediaPhone} {
    margin-left: 10px;
  }
`;

const ProjectPartners = () => (
  <Container>
    <Before />
    <Content>
      <StacheldrahtStyled left small />
      <Title>
        Projektpartner
      </Title>
      <InnerContent>
        <AboutThisProject>
          <p>
            Das Projekt entstand als Idee und auf Initiative des Bund f&uuml;r Bildung e. V.
            und wurde innerhalb des Vereins erstellt. Ganz herzlich bedanken m&ouml;chten wir uns
            bei der Bundesstiftung zur Aufarbeitung der SED-Diktatur, mit deren F&ouml;rderung
            wir das Projekt realisieren konnten.
          </p>
          <p>
            Ein besonderer Dank gilt ebenfalls unseren Fachberater*innen:
            <br />
            Prof. Dr. Eckhard Jesse von der TU Chemnitz und Prof. Dr. Christoph Kle&szlig;mann
            vom Zentrum f&uuml;r Zeithistorische Forschungen f&uuml;r die historischen Fragen;
            Thomas Knoll von CARE Deutschland-Luxemburg e.V., Projektleiter KIWI – Integrationsf&ouml;rderung
            an Schulen zur aktuellen Fl&uuml;chtlingssituation sowie Schulleiterin Martina Feindt
            und Schulleiter Falko Stolp zur Didaktik und P&auml;dagogik.
          </p>
        </AboutThisProject>
        <Logos>
          <BFBLogo labelStyles={LogoAlignStyles} imageContainerStyles={LogoImageContainerStyles} />
          <BALogoStyled labelStyles={LogoAlignStyles} imageContainerStyles={LogoImageContainerStyles} />
          <Fachpartner>
            <FachpartnerLabel className={CommonLogoLabelStyles}>
              Fachpartner
            </FachpartnerLabel>
            <FachpartnerLogoContainer>
              <FachpartnerLink href="https://www.care.de" target="_blank" rel="noopener nofollow external">
                <CareLogo src={CareLogoImg} />
              </FachpartnerLink>
              <FachpartnerLink href="https://www.care.de/nothilfe/themen/integration-in-deutschland/" target="_blank" rel="noopener nofollow external">
                <KiwiLogo src={KiwiLogoImg} />
              </FachpartnerLink>
            </FachpartnerLogoContainer>
          </Fachpartner>
        </Logos>
      </InnerContent>
    </Content>
  </Container>
);

export default ProjectPartners;



// WEBPACK FOOTER //
// ./src/components/ProjectPartners/ProjectPartners.js